import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, eur } from "@/lib/apiClient";
import { StatusBadge } from "@/components/StatusBadge";
import { effectiveQuoteState } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Pencil } from "lucide-react";
import { ReinvioProposte } from "@/components/ReinvioProposte";

const PRODUCTS = {
  rc_economia_solidale: { label: "RC Economia Solidale", short: "RC", cls: "bg-forest/10 text-forest border-forest/20" },
  mrv_volontariato: { label: "Tutela Multirischi Volontariato", short: "MRV", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  infortuni_economia_solidale: { label: "Infortuni Economia Solidale", short: "Infortuni", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  cat_nat_impresa: { label: "CAT NAT Assimoco", short: "CAT NAT", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  multirischi_beni: { label: "Tutela Multirischi Danni ai Beni", short: "Multir. Beni", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  casa_multirischi: { label: "Tutela Multirischi per l'Abitazione", short: "Casa", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  cooperazione_salute: { label: "Cooperazione Salute 4You", short: "4You", cls: "bg-red-50 text-red-700 border-red-200" },
  d_o: { label: "D&O Directors & Officers", short: "D&O", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};
const prodMeta = (code) => PRODUCTS[code] || { label: code || "—", short: code || "—", cls: "bg-muted text-muted-foreground border-border" };

export default function QuotesList({ vista }) {
  const [params] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState(vista || params.get("vista") || "all");
  useEffect(() => { const v = vista || params.get("vista"); if (v) setStatus(v); }, [vista, params]);
  useEffect(() => { api.get("/quotes").then(({ data }) => setQuotes(data)); }, []);

  const counts = useMemo(() => {
    const c = {};
    quotes.forEach((q) => { c[q.product_code] = (c[q.product_code] || 0) + 1; });
    return c;
  }, [quotes]);

  const PAID = ["pagato", "polizza_emessa", "perfezionato"];
  const SIGNED = ["accettato_cliente", "in_attesa_bonifico", ...PAID];
  const OPEN = ["bozza", "referral_inviato", "referral_rifiutato", "autorizzato", "preventivo_emesso"];
  const SENT = ["preventivo_emesso", "autorizzato"];
  const statusPred = {
    all: () => true,
    bozza: (q) => q.stato === "bozza",
    aperte: (q) => OPEN.includes(q.stato) && !(SENT.includes(q.stato) && q.inviato_cliente_at),
    proposte: (q) => (SENT.includes(q.stato) && q.inviato_cliente_at) || SIGNED.includes(q.stato),
    non_firmate: (q) => !SIGNED.includes(q.stato) && q.stato !== "non_perfezionato",
    non_pagate: (q) => !PAID.includes(q.stato) && q.stato !== "non_perfezionato",
  };
  const kpis = useMemo(() => ({
    all: quotes.length,
    bozza: quotes.filter(statusPred.bozza).length,
    aperte: quotes.filter(statusPred.aperte).length,
    proposte: quotes.filter(statusPred.proposte).length,
    non_firmate: quotes.filter(statusPred.non_firmate).length,
    non_pagate: quotes.filter(statusPred.non_pagate).length,
  }), [quotes]);

  const filtered = useMemo(
    () => quotes.filter((q) => (filter === "all" || q.product_code === filter) && statusPred[status](q)),
    [quotes, filter, status]);

  const KPI_CARDS = [
    { key: "all", label: "Totale preventivi", cls: "text-forest" },
    { key: "aperte", label: "Pratiche aperte", cls: "text-forest" },
    { key: "proposte", label: "Proposte emesse", cls: "text-terracotta" },
    { key: "bozza", label: "Bozze / da completare", cls: "text-slate-600" },
    { key: "non_firmate", label: "Non firmate", cls: "text-amber-600" },
    { key: "non_pagate", label: "Non pagate", cls: "text-rose-600" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 data-testid="quotes-title" className="font-heading text-3xl font-semibold text-forest">{status === "proposte" ? "Proposte emesse" : status === "aperte" ? "Pratiche aperte" : "I miei preventivi"}</h1>
        <p className="mt-1 text-muted-foreground">Tutte le tue pratiche assicurative su tutti i prodotti.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="quotes-kpi">
        {KPI_CARDS.map((k) => (
          <button key={k.key} data-testid={`kpi-${k.key}`} onClick={() => setStatus(k.key)}
            className={`rounded-xl border p-4 text-left transition-colors ${status === k.key ? "border-forest bg-sage/40" : "border-border/60 hover:bg-sage/20"}`}>
            <div className={`font-heading text-3xl font-semibold ${k.cls}`}>{kpis[k.key]}</div>
            <div className="mt-1 text-xs font-medium text-muted-foreground">{k.label}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="quotes-product-filter">
        <button data-testid="filter-all" onClick={() => setFilter("all")}
          className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${filter === "all" ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
          Tutti <span className="opacity-70">({quotes.length})</span>
        </button>
        {Object.keys(counts).map((code) => (
          <button key={code} data-testid={`filter-${code}`} onClick={() => setFilter(code)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${filter === code ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
            {prodMeta(code).short} <span className="opacity-70">({counts[code]})</span>
          </button>
        ))}
      </div>

      <ReinvioProposte />

      <Card className="overflow-hidden rounded-xl border-border/60">
        <Table>
          <TableHeader>
            <TableRow className="bg-sage/50">
              <TableHead>Pratica</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Prodotto</TableHead>
              <TableHead>Binario</TableHead>
              <TableHead className="text-right">Premio</TableHead>
              <TableHead className="text-right">Provvigione</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                <FileText className="mx-auto mb-2 h-6 w-6 opacity-40" /> Nessun preventivo.
              </TableCell></TableRow>
            )}
            {filtered.map((q) => {
              const m = prodMeta(q.product_code);
              return (
                <TableRow key={q.id} data-testid={`quote-list-row-${q.id}`} className="cursor-pointer">
                  <TableCell>
                    <Link to={`/preventivi/${q.id}`} className="font-medium text-forest hover:text-terracotta">
                      {q.codice_prev || `#${q.id.slice(-6).toUpperCase()}`}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`quote-cliente-${q.id}`} className="text-sm text-forest">
                      {q.cliente || "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`quote-product-badge-${q.id}`} className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.cls}`} title={m.label}>
                      {m.short}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${q.binario === "standard" ? "text-emerald-600" : "text-amber-600"}`}>
                      {q.binario === "standard" ? "Standard" : "Referral tecnico"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium">{eur(q.premio_finale)}</TableCell>
                  <TableCell className="text-right text-terracotta">{eur(q.provvigione?.importo)}</TableCell>
                  <TableCell><StatusBadge state={effectiveQuoteState(q)} /></TableCell>
                  <TableCell className="text-right">
                    <Link to={`/preventivi/${q.id}`} data-testid={`quote-open-${q.id}`}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-forest/30 px-3 py-1 text-xs font-medium text-forest transition-colors hover:bg-forest hover:text-white">
                      <Pencil className="h-3.5 w-3.5" /> Apri e modifica
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
